/**
 * Marktlage — Ersatz für die Marktwellen-Grafik (Audit 08.08.2026).
 *
 * Ronni: „Ich weiß nicht, ob die mir wirklich was Nützliches anzeigt. Ich bin
 * mir auch gar nicht sicher, ob das stimmt und hab da kein Vertrauen dazu."
 *
 * Beide Einwände waren berechtigt, aus drei verschiedenen Gründen:
 *
 * 1. DIE ZAHL STIMMTE ZEITWEISE NICHT. Die Stimmungs-Achse fiel an manchen
 *    Tagen still aus (siehe marktlage-read.ts) — das Urteil sprang, je
 *    nachdem welche Quelle zuletzt geschrieben hatte.
 *
 * 2. DIE GRAFIK ZEIGTE KEINE DATEN. Die Zyklus-Welle war eine gezeichnete
 *    Sinuskurve; der „Du bist hier"-Punkt wurde aus Stimmung × Marktphase auf
 *    diese Kurve abgebildet. Es gab keine Zeitachse, keinen gemessenen
 *    Verlauf und erst recht keine Prognose — sie SAH aus wie ein Chart, war
 *    aber die Illustration einer Kategorie. Genau deshalb verdient sie kein
 *    Vertrauen: Ein Bild, das mehr behauptet, als die Daten hergeben.
 *
 * 3. SIE GAB EINE HANDLUNGSANWEISUNG, DIE WIR WIDERLEGT HABEN. Sätze wie
 *    „Die Kaufzone für Qualität" oder „nicht am Top kaufen" sind
 *    Markt-Timing. Gemessen (Signal-Konzept §7.5): Die Faber-Regel zeigt auf
 *    der Bodenmenge EXAKT NULL Effekt (48,9 % gegen 49,2 %), Marktbreite
 *    bricht out-of-sample weg, und die Jahre, in denen das Setup wirklich
 *    versagte (2018, 2021), waren an keinem Regime-Indikator erkennbar.
 *
 * Diese Karte zeigt deshalb: echte Messwerte mit ihrem Alter, einen echten
 * Verlauf mit Zeitachse — und sagt ausdrücklich, wofür das NICHT taugt.
 */
import { useEffect, useMemo, useState } from 'react';
import { CloudSun } from 'lucide-react';

import {
  fetchAngstGier,
  fetchSpyDrawdownVerlauf,
  fetchVix,
  type MesswertMitAlter,
  type SpyVerlaufPunkt,
} from '@/lib/marktlage-read';
import { CARD } from '@/lib/design-tokens';

const W = 640;
const H = 120;
const PAD_L = 34;
const PAD_R = 8;
const PAD_T = 8;
const PAD_B = 18;

function altersHinweis(m: MesswertMitAlter | null): string {
  if (!m) return 'kein Wert';
  if (m.alter_tage === 0) return 'heute';
  if (m.alter_tage === 1) return 'gestern';
  return `${m.alter_tage} Tage alt`;
}

/** Farbe nach Abstand zum Hoch — für den antizyklischen Blick: tiefer = interessanter. */
function ddFarbe(dd: number): string {
  if (dd <= -15) return '#10b981';
  if (dd <= -7) return '#2dd4bf';
  if (dd <= -3) return '#a1a1aa';
  return '#f59e0b';
}

function SpyVerlaufChart({ punkte }: { punkte: SpyVerlaufPunkt[] }) {
  const { pfad, flaeche, minDd, letzter, ticks } = useMemo(() => {
    if (punkte.length < 2) {
      return { pfad: '', flaeche: '', minDd: 0, letzter: null as SpyVerlaufPunkt | null, ticks: [] as Array<{ x: number; label: string }> };
    }
    const min = Math.min(...punkte.map((p) => p.dd52_pct), -2);
    const y = (dd: number) => PAD_T + (dd / min) * (H - PAD_T - PAD_B);
    const x = (i: number) => PAD_L + (i / (punkte.length - 1)) * (W - PAD_L - PAD_R);

    const d = punkte.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.dd52_pct).toFixed(1)}`).join(' ');
    const f = `${d} L ${x(punkte.length - 1).toFixed(1)} ${PAD_T} L ${PAD_L} ${PAD_T} Z`;

    // Monats-Marken (jeweils erster Punkt eines neuen Monats).
    const t: Array<{ x: number; label: string }> = [];
    let letzterMonat = '';
    punkte.forEach((p, i) => {
      const monat = p.datum.slice(0, 7);
      if (monat !== letzterMonat) {
        letzterMonat = monat;
        t.push({ x: x(i), label: p.datum.slice(5, 7) });
      }
    });
    return { pfad: d, flaeche: f, minDd: min, letzter: punkte[punkte.length - 1]!, ticks: t.filter((_, i) => i % 2 === 0) };
  }, [punkte]);

  if (!pfad || !letzter) return null;
  const farbe = ddFarbe(letzter.dd52_pct);

  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[420px]" role="img"
        aria-label={`SPY-Abstand zum 52-Wochen-Hoch, aktuell ${letzter.dd52_pct.toFixed(1)} Prozent`}>
        {/* Nulllinie = Allzeithoch-Niveau */}
        <line x1={PAD_L} y1={PAD_T} x2={W - PAD_R} y2={PAD_T} stroke="#3f3f46" strokeWidth="1" strokeDasharray="3 3" />
        <text x={4} y={PAD_T + 4} fill="#71717a" fontSize="9">0 %</text>
        <text x={4} y={H - PAD_B} fill="#71717a" fontSize="9">{minDd.toFixed(0)} %</text>
        <path d={flaeche} fill={farbe} fillOpacity="0.12" />
        <path d={pfad} fill="none" stroke={farbe} strokeWidth="1.6" />
        {ticks.map((t, i) => (
          <text key={i} x={t.x} y={H - 4} fill="#52525b" fontSize="9" textAnchor="middle">{t.label}</text>
        ))}
      </svg>
    </div>
  );
}

export function MarktlageCard() {
  const [fg, setFg] = useState<MesswertMitAlter | null>(null);
  const [vix, setVix] = useState<MesswertMitAlter | null>(null);
  const [verlauf, setVerlauf] = useState<SpyVerlaufPunkt[] | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    void fetchAngstGier(ctrl.signal).then(setFg);
    void fetchVix(ctrl.signal).then(setVix);
    void fetchSpyDrawdownVerlauf(ctrl.signal).then(setVerlauf);
    return () => ctrl.abort();
  }, []);

  const aktuell = verlauf && verlauf.length > 0 ? verlauf[verlauf.length - 1]! : null;

  return (
    <div className={CARD}>
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-sm font-semibold text-zinc-200 flex items-center gap-2">
          <CloudSun className="size-4 text-sky-400" />
          Marktlage
        </h2>
        <span className="text-[10px] uppercase tracking-wide text-zinc-500">Kontext · kein Kauffilter</span>
      </div>

      {/* Die drei Achsen — jede mit ihrem eigenen Alter. Kein stiller Mittelwert. */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="rounded border border-zinc-800/70 bg-zinc-950/40 px-2 py-1.5">
          <div className="text-[10px] uppercase tracking-wide text-zinc-500">SPY zum Hoch</div>
          <div className="font-mono text-base tabular-nums" style={{ color: aktuell ? ddFarbe(aktuell.dd52_pct) : '#a1a1aa' }}>
            {aktuell ? `${aktuell.dd52_pct.toFixed(1)} %` : '—'}
          </div>
          <div className="text-[9px] text-zinc-500">{aktuell ? aktuell.datum : 'lädt …'}</div>
        </div>
        <div className="rounded border border-zinc-800/70 bg-zinc-950/40 px-2 py-1.5">
          <div className="text-[10px] uppercase tracking-wide text-zinc-500">VIX</div>
          <div className="font-mono text-base tabular-nums text-zinc-200">{vix ? vix.wert.toFixed(1) : '—'}</div>
          <div className={`text-[9px] ${vix && vix.alter_tage > 3 ? 'text-amber-400' : 'text-zinc-500'}`}>
            {altersHinweis(vix)}
          </div>
        </div>
        <div className="rounded border border-zinc-800/70 bg-zinc-950/40 px-2 py-1.5">
          <div className="text-[10px] uppercase tracking-wide text-zinc-500">Angst/Gier</div>
          <div className="font-mono text-base tabular-nums text-zinc-200">{fg ? Math.round(fg.wert) : '—'}</div>
          <div className={`text-[9px] ${fg && fg.alter_tage > 3 ? 'text-amber-400' : 'text-zinc-500'}`}>
            {altersHinweis(fg)}
          </div>
        </div>
      </div>

      {verlauf === null && <div className="skeleton h-24 w-full" />}
      {verlauf !== null && verlauf.length > 1 && <SpyVerlaufChart punkte={verlauf} />}
      {verlauf !== null && verlauf.length <= 1 && (
        <div className="text-xs text-zinc-500">Zu wenig SPY-Historie für den Verlauf.</div>
      )}

      <p className="mt-2 text-[11px] text-zinc-400 leading-snug">
        Abstand des Gesamtmarkts (SPY) zu seinem eigenen 52-Wochen-Hoch, letzte
        12 Monate. Echte Kursdaten, echte Zeitachse — kein gezeichneter Zyklus.
      </p>

      <details className="mt-2 group">
        <summary className="cursor-pointer list-none text-[11px] text-zinc-500 hover:text-zinc-300 flex items-center gap-1">
          <span className="inline-block transition-transform group-open:rotate-90">▸</span>
          Wofür das taugt — und wofür ausdrücklich nicht
        </summary>
        <div className="mt-1.5 space-y-1.5 text-[11px] text-zinc-400 leading-snug border-t border-zinc-800/60 pt-1.5">
          <p>
            <strong className="text-zinc-300">Kein Kauffilter.</strong> Gemessen über
            21 Jahre: Ob der SPY über oder unter seiner 200-Tage-Linie steht, macht für
            den Erfolg eines Boden-Setups <strong>exakt keinen Unterschied</strong>
            {' '}(48,9 % gegen 49,2 %). Auch Marktbreite hilft nicht — der Effekt
            verschwindet ab 2016. Die beiden Jahre, in denen die Regel wirklich versagt
            hat (2018, 2021), liefen bei grünen Ampeln auf ganzer Linie.
          </p>
          <p>
            <strong className="text-zinc-300">Wofür es taugt:</strong> die
            Positionsgröße. Steht der Markt nahe seinem Hoch, ist der tiefer gefallene
            Einzelwert der bessere Kauf — das ist der einzige Regime-Effekt, der beide
            Zeiträume überlebt hat. Und ein hoher VIX heißt: dieselbe Stop-Weite kostet
            mehr Geld, also kleinere Stückzahl.
          </p>
          <p className="text-zinc-500 italic">
            Die Kaufentscheidung selbst trifft die Zustandsmaschine je Wert, nicht diese
            Karte. Steht hier „Gier" und die Zustandsmaschine sagt „Kaufzone offen",
            gilt die Zustandsmaschine.
          </p>
        </div>
      </details>
    </div>
  );
}
